import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { DeclarePayload } from '../../core/entity/declaration.types';
import { ModalComponent } from '../modal/modal';
import { DeclarationDialogResult } from './declaration-dialog';

/**
 * Ops adding a bank account a statement revealed (FLOW-280) — the inline form
 * that sat in the Bank accounts card, moved into a modal.
 *
 * Like {@link DeclarationDialogComponent} it **builds a payload and does not
 * write**: the panel owns the Ops door and the re-read that follows (adding an
 * account retracts a standing affirmation server-side).
 */
@Component({
  selector: 'app-account-dialog',
  imports: [
    ModalComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  templateUrl: './account-dialog.html',
  styleUrl: './declaration-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountDialogComponent {
  private readonly dialogRef =
    inject<MatDialogRef<AccountDialogComponent, DeclarationDialogResult>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);

  /**
   * A select and not a checkbox: "Not sure" has to be sayable, and an unticked
   * box would record "no trading on this account" — a claim nobody made.
   */
  protected readonly tradingStates = [
    { value: 'YES', label: 'Yes' },
    { value: 'NO', label: 'No' },
    { value: 'NOT_SURE', label: 'Not sure' },
  ];

  protected readonly form = this.fb.group({
    bank_name: [''],
    branch_code: [''],
    account_number: [''],
    account_holder_name: [''],
    carries_trading_activity: [''],
  });

  protected submit(): void {
    const v = this.form.getRawValue();
    const bank = (v.bank_name ?? '').trim();
    const branch = (v.branch_code ?? '').trim();
    const number = (v.account_number ?? '').trim();
    const holder = (v.account_holder_name ?? '').trim();
    if (!bank || !branch || !number || !holder) {
      const c = this.form.controls;
      for (const control of [c.bank_name, c.branch_code, c.account_number, c.account_holder_name]) {
        if (!(control.value ?? '').trim()) control.setErrors({ required: true });
        control.markAsTouched();
      }
      return;
    }
    this.dialogRef.close({
      payload: {
        kind: 'ACCOUNT',
        bank_name: bank,
        branch_code: branch,
        account_number: number,
        account_holder_name: holder,
        carries_trading_activity: (v.carries_trading_activity ?? '') as never,
      } as DeclarePayload,
    });
  }
}
